/**
 * Portfolio Motion System — src/utils/motion.js
 * ─────────────────────────────────────────────────────────────
 * Central GSAP utility library for consistent, premium animations
 * across the entire portfolio.
 *
 * Rules:
 *  - Every helper respects prefers-reduced-motion (guard at call-site)
 *  - Functions return the GSAP tween/timeline so callers can kill it
 *  - Use inside gsap.context() for proper cleanup
 */

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* ─── Easings ────────────────────────────────────────────────── */
export const EASE = {
  gentle:       "power2.out",
  standard:     "power3.out",
  strong:       "power4.out",
  expo:         "expo.out",
  spring:       "back.out(1.4)",
  springStrong: "back.out(2.2)",
  sine:         "sine.inOut",
};

/* ─── Duration scale ─────────────────────────────────────────── */
export const DUR = {
  fast:     0.35,
  normal:   0.55,
  standard: 0.72,
  slow:     1.0,
  cinematic:1.4,
};

/* ─── Reduced-motion guard ──────────────────────────────────── */
export const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ─── Default ScrollTrigger config ─────────────────────────── */
const ST_DEFAULTS = {
  start: "top 80%",
  toggleActions: "play none none reset",
};

/**
 * revealSectionHeader
 * Cascade: eyebrow → title → subtitle
 * Works with .section-eyebrow/.section-title/.section-subtitle selectors
 * (and abt- / cs- / projects- variants).
 */
export function revealSectionHeader(headerEl, triggerEl, stOverrides = {}) {
  if (!headerEl) return null;

  const eyebrow  = headerEl.querySelector(
    ".section-eyebrow, .abt-eyebrow, .cs-section-label, .projects-eyebrow"
  );
  const title    = headerEl.querySelector(
    ".section-title, .abt-heading, .projects-title, .carousel-title, .cs-hero-title"
  );
  const subtitle = headerEl.querySelector(
    ".section-subtitle, .projects-subtitle, .cs-hero-subtitle"
  );

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: triggerEl || headerEl,
      ...ST_DEFAULTS,
      start: "top 79%",
      ...stOverrides,
    },
  });

  if (eyebrow)  tl.from(eyebrow,  { opacity: 0, y: 14, duration: DUR.normal,   ease: EASE.standard });
  if (title)    tl.from(title,    { opacity: 0, y: 38, duration: DUR.standard, ease: EASE.strong   }, "-=0.24");
  if (subtitle) tl.from(subtitle, { opacity: 0, y: 18, duration: DUR.normal,   ease: EASE.standard }, "-=0.28");

  return tl;
}

/**
 * revealCards
 * Staggered card/panel entrance from below.
 */
export function revealCards(cards, stOverrides = {}, tweenOverrides = {}) {
  if (!cards?.length) return null;
  return gsap.from(cards, {
    opacity: 0,
    y: 48,
    duration: DUR.standard,
    stagger: 0.09,
    ease: EASE.standard,
    ...tweenOverrides,
    scrollTrigger: {
      trigger: cards[0],
      ...ST_DEFAULTS,
      start: "top 88%",
      ...stOverrides,
    },
  });
}

/**
 * revealFromBelow
 * Single element fade+rise.
 */
export function revealFromBelow(el, delay = 0, stOverrides = {}, tweenOverrides = {}) {
  if (!el) return null;
  return gsap.from(el, {
    opacity: 0,
    y: 40,
    duration: DUR.standard,
    delay,
    ease: EASE.standard,
    ...tweenOverrides,
    scrollTrigger: {
      trigger: el,
      ...ST_DEFAULTS,
      start: "top 88%",
      ...stOverrides,
    },
  });
}

/**
 * drawAccentLine
 * scaleY 0 → 1 for a vertical accent bar.
 * The element must have `transform-origin: top center` via CSS or inline.
 */
export function drawAccentLine(el, stOverrides = {}) {
  if (!el) return null;
  return gsap.from(el, {
    scaleY: 0,
    duration: 0.85,
    ease: EASE.strong,
    transformOrigin: "top center",
    scrollTrigger: {
      trigger: el,
      ...ST_DEFAULTS,
      start: "top 88%",
      ...stOverrides,
    },
  });
}

/**
 * animateCounter
 * Counts a DOM element's textContent from `from` to `to`.
 * Passes a scrollTrigger so the counter starts on scroll.
 */
export function animateCounter(el, {
  from = 0,
  to,
  duration = 1.4,
  suffix = "",
  decimals = 0,
  scrollTrigger: stVars,
} = {}) {
  if (!el || to === undefined) return null;
  const obj = { val: from };
  return gsap.to(obj, {
    val: to,
    duration,
    ease: EASE.expo,
    scrollTrigger: stVars,
    onUpdate() {
      const v = decimals > 0
        ? obj.val.toFixed(decimals)
        : Math.round(obj.val).toString();
      el.textContent = v + suffix;
    },
  });
}

/**
 * heroParallax
 * Scroll-driven parallax for hero sub-elements.
 * Call outside gsap.context() or pass scrub-safe targets.
 */
export function heroParallax(el, yPercent = -14, stOverrides = {}) {
  if (!el) return null;
  return gsap.to(el, {
    yPercent,
    ease: "none",
    scrollTrigger: {
      trigger: document.documentElement,
      start: "top top",
      end: "40% top",
      scrub: 1.4,
      ...stOverrides,
    },
  });
}

/**
 * softFloat
 * Subtle infinite float/breathe for ambient elements.
 * Returns the tween so it can be killed on unmount.
 */
export function softFloat(el, { yMove = 10, duration = 5.5, delay = 0 } = {}) {
  if (!el) return null;
  return gsap.to(el, {
    y: yMove,
    duration,
    delay,
    ease: EASE.sine,
    yoyo: true,
    repeat: -1,
  });
}

/**
 * blurReveal
 * Reveal from blur + opacity — for cinematic headline moments.
 */
export function blurReveal(el, stOverrides = {}, tweenOverrides = {}) {
  if (!el) return null;
  return gsap.from(el, {
    opacity: 0,
    filter: "blur(8px)",
    y: 24,
    duration: DUR.slow,
    ease: EASE.strong,
    clearProps: "filter",
    ...tweenOverrides,
    scrollTrigger: {
      trigger: el,
      ...ST_DEFAULTS,
      start: "top 82%",
      ...stOverrides,
    },
  });
}

/**
 * pageEnter
 * Call on route mount to fade+rise the entire page container.
 * No ScrollTrigger — runs immediately on mount.
 */
export function pageEnter(el, delay = 0) {
  if (!el) return null;
  return gsap.from(el, {
    opacity: 0,
    y: 22,
    duration: DUR.slow,
    delay,
    ease: EASE.strong,
    clearProps: "opacity,transform",
  });
}
